import { cn } from '@/lib/utils';
import { CheckCircleSolid, ChevronRight } from '@mynaui/icons-react';
import { FunctionComponent, MouseEvent, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useIsActive } from '.';

export const Suboption: FunctionComponent<{
  label: string;
  href?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  suboptions?: { label: string; href?: string; icon?: React.ReactNode; onClick?: () => void; checked?: boolean }[];
  checked?: boolean;
  onPrefetch?: () => void;
}> = ({ label, href, icon, onClick, destructive, suboptions, checked, onPrefetch }) => {
  const navigate = useNavigate();
  const isActive = useIsActive(href, true);
  const [isHovered, setIsHovered] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const _handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.();
      if (!href) return;
      e.preventDefault();

      if (e.metaKey || e.ctrlKey) window.open(href, '_blank');
      else navigate(href);
    },
    [onClick, href, navigate],
  );

  const _handleMouseEnter = useCallback(() => {
    if (onPrefetch) {
      prefetchTimeout.current = setTimeout(onPrefetch, 50);
    }
    if (suboptions && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        x: rect.right, // 8px offset from the right edge
        y: rect.top,
      });
      setIsHovered(true);
    }
  }, [suboptions, onPrefetch]);

  const _handleMouseLeave = useCallback(() => {
    clearTimeout(prefetchTimeout.current);
    setIsHovered(false);
  }, []);

  return (
    <>
      <button
        ref={buttonRef}
        data-href={href}
        onClick={_handleClick}
        onMouseEnter={_handleMouseEnter}
        onMouseLeave={_handleMouseLeave}
        className={cn(
          'flex w-full relative items-center font-medium gap-2 border border-transparent overflow-hidden rounded-md px-2.5 pr-2 text-left outline-none ring-sidebar-ring focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 text-slate-700/90 dark:text-stone-200  h-8 text-sm  [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2',
          isActive
            ? 'bg-white dark:bg-stone-900 border-slate-200 dark:border-stone-800 text-slate-900 shadow-sm/3'
            : 'active:bg-slate-200/50 active:text-slate-700 hover:bg-slate-200/25 dark:active:bg-stone-800/50 dark:active:text-stone-200 dark:hover:bg-stone-800/25',
          destructive && 'text-rose-500 hover:bg-rose-500/5 active:bg-rose-500/10 active:text-rose-600',
        )}>
        {icon}
        <span className='flex-1 font-[450]'>{label}</span>
        {suboptions && <ChevronRight className='size-4' />}
        {checked && <CheckCircleSolid className='size-4' />}
      </button>
      {suboptions &&
        isHovered &&
        createPortal(
          <div
            className='fixed z-50 bg-white dark:bg-stone-900 border border-slate-200 dark:border-stone-800 rounded-md shadow-xl p-1 min-w-48'
            style={{
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={_handleMouseLeave}>
            {suboptions.map((suboption) => {
              return (
                <Suboption
                  key={suboption.label}
                  label={suboption.label}
                  href={suboption.href}
                  icon={suboption.icon}
                  onClick={suboption.onClick}
                  checked={suboption.checked}
                />
              );
            })}
          </div>,
          document.getElementById('tooltip')!,
        )}
    </>
  );
};
